// src/admin/presets/PresetReviewList.tsx
import React, { useState, useEffect } from 'react';
import PresetReviewCard from './PresetReviewCard';
import { api } from './api';
import { Button } from '../../components/ui/button';

interface PresetReviewListProps {
  status?: string;
  onUpdate?: () => void;
  readOnly?: boolean;
}

/**
 * Lista kandydatów presetów do przeglądu
 */
const PresetReviewList: React.FC<PresetReviewListProps> = ({
  status = 'pending',
  onUpdate,
  readOnly = false,
}) => {
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadCandidates();
  }, [status, page]);

  const loadCandidates = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.getCandidates({
        status,
        limit,
        offset: page * limit,
      });
      setCandidates(data);
    } catch (error: any) {
      setError(error.message);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (candidateId: string) => {
    try {
      await api.acceptCandidate(candidateId);
      
      // Usuń z listy
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      setSelectedCandidate(null);
      
      // Odśwież statystyki
      if (onUpdate) onUpdate();
    } catch (error: any) {
      alert(`Błąd akceptacji: ${error.message}`);
    }
  };

  const handleReject = async (candidateId: string) => {
    try {
      await api.rejectCandidate(candidateId);
      
      // Usuń z listy
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      setSelectedCandidate(null);
      
      // Odśwież statystyki
      if (onUpdate) onUpdate();
    } catch (error: any) {
      alert(`Błąd odrzucania: ${error.message}`);
    }
  };

  const handleUpdate = async (candidateId: string, updates: any) => {
    try {
      await api.updateCandidate(candidateId, updates);
      
      // Zaktualizuj lokalnie
      setCandidates(prev => prev.map(c => 
        c.id === candidateId 
          ? { ...c, preset: { ...c.preset, ...updates } }
          : c
      ));
      
      alert('Kandydat zaktualizowany!');
    } catch (error: any) {
      alert(`Błąd aktualizacji: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Błąd: {error}
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Brak kandydatów w statusie: {status}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Lista kandydatów */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {candidates.map((candidate) => (
          <div
            key={candidate.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedCandidate?.id === candidate.id
                ? 'border-blue-500 shadow-lg'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedCandidate(candidate)}
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-gray-900">
                {candidate.preset.name}
              </h3>
              <span className={`px-2 py-1 text-xs rounded ${
                candidate.preset.category === 'ekspozytor' ? 'bg-blue-100 text-blue-800' :
                candidate.preset.category === 'pojemnik' ? 'bg-green-100 text-green-800' :
                candidate.preset.category === 'kaseton' ? 'bg-purple-100 text-purple-800' :
                candidate.preset.category === 'gablota' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {candidate.preset.category}
              </span>
            </div>
            
            <div className="text-sm text-gray-600 space-y-1">
              <div>
                <span className="font-medium">Materiał:</span>{' '}
                {candidate.preset.material?.type} {candidate.preset.material?.thickness}mm
              </div>
              <div>
                <span className="font-medium">Wymiary:</span>{' '}
                {candidate.preset.geometry?.width} x {candidate.preset.geometry?.height} x {candidate.preset.geometry?.depth} mm
              </div>
              {candidate.pricing && (
                <div>
                  <span className="font-medium">Cena netto:</span>{' '}
                  <span className="text-green-600 font-semibold">
                    {candidate.pricing.totalNet?.toFixed(2)} PLN
                  </span>
                </div>
              )}
              <div className="text-xs text-gray-400">
                Źródło: {new URL(candidate.sourceUrl).hostname}
              </div>
            </div>

            {!readOnly && (
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccept(candidate.id);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  ✓ Akceptuj
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(candidate.id);
                  }}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  ✗ Odrzuć
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Szczegóły wybranego kandydata */}
      {selectedCandidate && (
        <PresetReviewCard
          candidate={selectedCandidate}
          onAccept={!readOnly ? handleAccept : undefined}
          onReject={!readOnly ? handleReject : undefined}
          onUpdate={!readOnly ? handleUpdate : undefined}
          onClose={() => setSelectedCandidate(null)}
        />
      )}

      {/* Paginacja */}
      <div className="flex justify-center gap-2 mt-6">
        <Button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          variant="outline"
        >
          ← Poprzednia
        </Button>
        <span className="py-2 px-4">
          Strona {page + 1}
        </span>
        <Button
          onClick={() => setPage(page + 1)}
          disabled={candidates.length < limit}
          variant="outline"
        >
          Następna →
        </Button>
      </div>
    </div>
  );
};

export default PresetReviewList;
